import { LOCALE_OPTIONS } from '@vav/i18n';
import { useI18n } from '../i18n.tsx';

interface Props {
  onClose: () => void;
}

/**
 * Language picker.
 *
 * Detection from the browser is right by default and wrong often enough to
 * need an override: a shared machine, a work laptop provisioned in another
 * country, a browser whose language was set once and forgotten. Without a way
 * out, a user who lands in the wrong language cannot read the settings that
 * would fix it.
 *
 * Each language is listed in itself, and each row carries its own `lang` and
 * `dir` so the name renders in its own script and direction regardless of what
 * the surrounding UI is currently set to.
 */
export function LanguageDialog({ onClose }: Props) {
  const { locale, setLocale, t } = useI18n();

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label={t('language.title')}>
        <header className="dialog__header">
          <h2>{t('language.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('invite.close')}>
            ✕
          </button>
        </header>

        <div className="results">
          {LOCALE_OPTIONS.map((option) => (
            <button
              key={option.tag}
              className="result"
              lang={option.tag}
              aria-current={option.tag === locale}
              onClick={() => {
                setLocale(option.tag);
                onClose();
              }}
            >
              <span>
                <strong>{option.endonym}</strong>
              </span>
              {option.tag === locale && <span className="check">✓</span>}
            </button>
          ))}
        </div>

        <p className="hint">{t('language.note')}</p>
      </div>
    </div>
  );
}
